// Form model for add/edit beneficiary (§5, §6): block shapes, per-block
// validation with the spec's messages, and conversion to the atomic save
// payload (§5.4, §17.9).
import type {
  AccountKind,
  MobileSubType,
  NewAccountInput,
} from '@/types/phase2.types';
import {
  isValidAlias,
  isValidIfsc,
  isValidIban,
  isValidIntlAccount,
  isValidLocalAccountNumber,
  isValidMobile,
  isValidSwift,
  isValidUpiId,
} from '@/utils/phase2Validation';
import { ROUTING_CODES } from '@/mock/data';

export type BlockErrors = Record<string, string>;

interface BaseBlock {
  key: string;
}

export interface MobileBlock extends BaseBlock {
  kind: 'MOBILE_TRANSFER';
  subType: MobileSubType | '';
  upiId: string;
  mobile: string;
  merchantName: string;
  operator: string;
  circle: string;
  billCategory: string;
  biller: string;
  consumerNumber: string;
  holderName: string;
  verified: 'NONE' | 'FOUND' | 'FLAGGED';
}

export interface LocalBlock extends BaseBlock {
  kind: 'LOCAL_TRANSFER';
  bankCode: string;
  bankName: string;
  ifscPrefix: string;
  branchName: string;
  branchCity: string;
  ifsc: string;
  accountNumber: string;
  accountNumber2: string;
  accountName: string;
  lookup: 'IDLE' | 'CHECKING' | 'FOUND' | 'NOT_FOUND';
  showIfscDirect: boolean;
}

export interface InternationalBlock extends BaseBlock {
  kind: 'INTERNATIONAL';
  countryCode: string;
  countryName: string;
  currency: string;
  ibanRequired: boolean;
  bankName: string;
  accountName: string;
  idMode: '' | 'ACCOUNT' | 'IBAN';
  iban: string;
  accountNumber: string;
  routingCode: string;
  swift: string;
  bankLine1: string;
  bankLine2: string;
  bankPostCode: string;
  bankTown: string;
  accLine1: string;
  accLine2: string;
  accPostCode: string;
  accTown: string;
}

export type Block = MobileBlock | LocalBlock | InternationalBlock;

let blockCounter = 0;
const nextKey = (): string => `block_${++blockCounter}_${Date.now().toString(36)}`;

export const emptyMobileBlock = (): MobileBlock => ({
  key: nextKey(), kind: 'MOBILE_TRANSFER', subType: '', upiId: '', mobile: '',
  merchantName: '', operator: '', circle: '', billCategory: '', biller: '',
  consumerNumber: '', holderName: '', verified: 'NONE',
});

export const emptyLocalBlock = (): LocalBlock => ({
  key: nextKey(), kind: 'LOCAL_TRANSFER', bankCode: '', bankName: '', ifscPrefix: '',
  branchName: '', branchCity: '', ifsc: '', accountNumber: '', accountNumber2: '',
  accountName: '', lookup: 'IDLE', showIfscDirect: false,
});

export const emptyInternationalBlock = (): InternationalBlock => ({
  key: nextKey(), kind: 'INTERNATIONAL', countryCode: '', countryName: '', currency: '',
  ibanRequired: false, bankName: '', accountName: '', idMode: '', iban: '',
  accountNumber: '', routingCode: '', swift: '', bankLine1: '', bankLine2: '',
  bankPostCode: '', bankTown: '', accLine1: '', accLine2: '', accPostCode: '', accTown: '',
});

export const emptyBlockFor = (kind: AccountKind): Block =>
  kind === 'MOBILE_TRANSFER' ? emptyMobileBlock() : kind === 'LOCAL_TRANSFER' ? emptyLocalBlock() : emptyInternationalBlock();

// ---------- alias ----------

export const aliasError = (alias: string, existingAliases: string[]): string => {
  const trimmed = alias.trim();
  if (!isValidAlias(trimmed)) return 'Enter 2 to 40 letters, numbers, spaces or dots.';
  if (existingAliases.some((a) => a.toLowerCase() === trimmed.toLowerCase())) {
    return 'You already have a beneficiary with this name.';
  }
  return '';
};

// ---------- Mobile Transfer (§6.1) ----------

const ROUTING_COUNTRIES = new Set(['US', 'GB', 'AU', 'CA']);

export const mobileBlockErrors = (block: MobileBlock): BlockErrors => {
  const errors: BlockErrors = {};
  if (!block.subType) {
    errors.subType = 'Select a type.';
    return errors;
  }
  const needsVerification = block.subType === 'UPI_ID' || block.subType === 'MOBILE_NUMBER';

  if (block.subType === 'UPI_ID' || block.subType === 'MERCHANT_UPI') {
    if (!isValidUpiId(block.upiId)) errors.upiId = 'Enter a valid UPI ID, like name@bank.';
  }
  if (block.subType === 'MOBILE_NUMBER' || block.subType === 'MOBILE_RECHARGE') {
    if (!isValidMobile(block.mobile)) errors.mobile = 'Enter a valid 10-digit mobile number.';
  }
  if (needsVerification) {
    if (block.verified === 'NONE' && !errors.upiId && !errors.mobile) {
      errors.verified =
        block.subType === 'UPI_ID' ? 'Verify the UPI ID first.' : 'Verify the mobile number first.';
    }
    if (block.verified !== 'NONE' && !block.holderName.trim()) {
      errors.holderName = 'Required';
    }
  }
  if (block.subType === 'MOBILE_RECHARGE') {
    if (!block.operator) errors.operator = 'Required';
    if (!block.circle) errors.circle = 'Required';
    if (!block.holderName.trim()) errors.holderName = 'Required';
  }
  if (block.subType === 'BILL_PAYMENT') {
    if (!block.billCategory) errors.billCategory = 'Required';
    if (!block.biller) errors.biller = 'Required';
    if (!block.consumerNumber.trim()) errors.consumerNumber = 'Required';
    if (!block.holderName.trim()) errors.holderName = 'Required';
  }
  return errors;
};

export const isMobileComplete = (block: MobileBlock): boolean =>
  Object.keys(mobileBlockErrors(block)).length === 0;

// ---------- Local Transfer (§6.2) ----------

export const localBlockErrors = (block: LocalBlock): BlockErrors => {
  const errors: BlockErrors = {};
  if (!block.bankCode) errors.bank = 'Select a bank.';
  if (!block.ifsc) {
    errors.ifsc = 'Pick a branch or enter the IFSC.';
  } else if (!isValidIfsc(block.ifsc)) {
    errors.ifsc = 'IFSC looks like HDFC0001234 — 4 letters, 0, then 6 letters or digits.';
  }
  if (!isValidLocalAccountNumber(block.accountNumber)) {
    errors.accountNumber = 'Enter a valid account number (9 to 18 digits).';
  }
  if (block.accountNumber2 !== block.accountNumber || !block.accountNumber2) {
    if (block.accountNumber2) errors.accountNumber2 = 'Account Numbers do not match';
    else if (!errors.accountNumber) errors.accountNumber2 = 'Required';
  }
  if (!errors.ifsc && !errors.accountNumber && !errors.accountNumber2) {
    if (block.lookup === 'NOT_FOUND') {
      errors.lookup = "We couldn't find this account. Check the number and IFSC.";
    } else if (block.lookup !== 'FOUND' || !block.accountName) {
      errors.lookup = 'Wait for the account check to finish.';
    }
  }
  return errors;
};

export const isLocalComplete = (block: LocalBlock): boolean =>
  Object.keys(localBlockErrors(block)).length === 0;

// ---------- International Transfer (§6.3) ----------

export const internationalBlockErrors = (block: InternationalBlock): BlockErrors => {
  const errors: BlockErrors = {};
  if (!block.countryCode) errors.country = 'Select a country.';
  if (!block.bankName.trim()) errors.bankName = 'Required';
  if (!block.accountName.trim()) errors.accountName = 'Required';

  if (block.ibanRequired) {
    if (!isValidIban(block.iban)) errors.iban = "This IBAN isn't valid. Check it with the beneficiary.";
  } else {
    if (!block.idMode) errors.idMode = 'Pick account number or IBAN.';
    else if (block.idMode === 'IBAN' && !isValidIban(block.iban)) {
      errors.iban = "This IBAN isn't valid. Check it with the beneficiary.";
    } else if (block.idMode === 'ACCOUNT' && !isValidIntlAccount(block.accountNumber)) {
      errors.accountNumber = 'Enter 4 to 34 letters or digits.';
    }
  }

  const routing = ROUTING_CODES[block.countryCode];
  if (routing && 'digits' in routing) {
    if (!new RegExp(`^\\d{${routing.digits}}$`).test(block.routingCode.trim())) {
      errors.routingCode = `${routing.label} is ${routing.digits} digits.`;
    }
  } else if (block.routingCode.trim() && !/^[A-Za-z0-9]{1,20}$/.test(block.routingCode.trim())) {
    errors.routingCode = 'Letters and digits only, up to 20.';
  }

  const swiftOptional = ROUTING_COUNTRIES.has(block.countryCode);
  if (!block.swift.trim()) {
    if (!swiftOptional) errors.swift = 'SWIFT/BIC is 8 or 11 characters, like HDFCINBBXXX.';
  } else if (!isValidSwift(block.swift)) {
    errors.swift = 'SWIFT/BIC is 8 or 11 characters, like HDFCINBBXXX.';
  }

  // Bank address is required only when SWIFT is empty (§6.3).
  if (!block.swift.trim()) {
    if (!block.bankLine1.trim() || !block.bankTown.trim()) {
      errors.bankAddress = 'Give the bank address, or a SWIFT/BIC above.';
    }
  }
  if (!block.accLine1.trim()) errors.accLine1 = 'Required';
  if (!block.accTown.trim()) errors.accTown = 'Required';
  return errors;
};

export const isInternationalComplete = (block: InternationalBlock): boolean =>
  Object.keys(internationalBlockErrors(block)).length === 0;

// ---------- shared ----------

export const blockErrors = (block: Block): BlockErrors => {
  switch (block.kind) {
    case 'MOBILE_TRANSFER':
      return mobileBlockErrors(block);
    case 'LOCAL_TRANSFER':
      return localBlockErrors(block);
    case 'INTERNATIONAL':
      return internationalBlockErrors(block);
  }
};

export const isBlockComplete = (block: Block): boolean => Object.keys(blockErrors(block)).length === 0;

export const toNewAccountInput = (block: Block): NewAccountInput => {
  switch (block.kind) {
    case 'MOBILE_TRANSFER': {
      const holder =
        block.subType === 'MERCHANT_UPI'
          ? block.merchantName.trim() || block.upiId.split('@')[0]
          : block.holderName.trim();
      return {
        kind: 'MOBILE_TRANSFER',
        subType: block.subType as MobileSubType,
        holderName: holder,
        upiId:
          block.subType === 'UPI_ID' || block.subType === 'MERCHANT_UPI'
            ? block.upiId.trim().toLowerCase()
            : undefined,
        mobile:
          block.subType === 'MOBILE_NUMBER' || block.subType === 'MOBILE_RECHARGE'
            ? block.mobile.trim()
            : undefined,
        merchantName: block.subType === 'MERCHANT_UPI' ? block.merchantName.trim() || undefined : undefined,
        operator: block.subType === 'MOBILE_RECHARGE' ? block.operator : undefined,
        circle: block.subType === 'MOBILE_RECHARGE' ? block.circle : undefined,
        billCategory: block.subType === 'BILL_PAYMENT' ? block.billCategory : undefined,
        biller: block.subType === 'BILL_PAYMENT' ? block.biller : undefined,
        consumerNumber: block.subType === 'BILL_PAYMENT' ? block.consumerNumber.trim() : undefined,
        flagged: block.verified === 'FLAGGED',
      };
    }
    case 'LOCAL_TRANSFER':
      return {
        kind: 'LOCAL_TRANSFER',
        bankCode: block.bankCode,
        bankName: block.bankName,
        branchName: block.branchName || undefined,
        branchCity: block.branchCity || undefined,
        ifsc: block.ifsc.trim().toUpperCase(),
        accountNumber: block.accountNumber.trim(),
        accountName: block.accountName,
      };
    case 'INTERNATIONAL': {
      const hasBankAddress = Boolean(
        block.bankLine1.trim() || block.bankLine2.trim() || block.bankPostCode.trim() || block.bankTown.trim()
      );
      const usesIban = block.ibanRequired || block.idMode === 'IBAN';
      return {
        kind: 'INTERNATIONAL',
        countryCode: block.countryCode,
        bankName: block.bankName.trim(),
        accountName: block.accountName.trim(),
        iban: usesIban ? block.iban.replace(/\s/g, '').toUpperCase() : undefined,
        accountNumber: usesIban ? undefined : block.accountNumber.trim(),
        routingCode: block.routingCode.trim() || undefined,
        swift: block.swift.trim().toUpperCase() || undefined,
        currency: block.currency,
        bankAddress: hasBankAddress
          ? {
              line1: block.bankLine1.trim(),
              line2: block.bankLine2.trim() || undefined,
              postCode: block.bankPostCode.trim() || undefined,
              town: block.bankTown.trim() || undefined,
            }
          : undefined,
        accountAddress: {
          line1: block.accLine1.trim(),
          line2: block.accLine2.trim() || undefined,
          postCode: block.accPostCode.trim() || undefined,
          town: block.accTown.trim(),
        },
      };
    }
  }
};
